// src/App.jsx
import React, { useState, useEffect, lazy, Suspense } from 'react';
import './styles/global.css';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';

import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';

import Home from './pages/Home';
import Shop, { productData } from './pages/Shop';

// 其他较少访问的页面继续懒加载以优化首屏性能
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Showcase = lazy(() => import('./pages/Showcase'));
const Story = lazy(() => import('./pages/Story'));
const Draft = lazy(() => import('./pages/Draft'));
const Press = lazy(() => import('./pages/Press'));
const QA = lazy(() => import('./pages/QA'));
const Connect = lazy(() => import('./pages/Connect'));
const Stockists = lazy(() => import('./pages/Stockists'));
const Cart = lazy(() => import('./pages/Cart'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));

// 加载占位符
function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      fontFamily: 'var(--font-sans)'
    }}>
      Loading...
    </div>
  );
}

function AppContent() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // 页面路由逻辑
  useEffect(() => {
    const handleLocationChange = () => {
      // 提取真实页面路径并去除首斜杠，空路径默认为 home
      const path = window.location.pathname.replace(/^\//, '') || 'home';
      setCurrentPage(path);
      window.scrollTo(0, 0); // 路由切换时自动滚动到顶部，防止页面显得一片空白
    };

    // 全局点击拦截器：拦截同源的 <a> 链接，使用 pushState 进行无刷新切换
    const handleAnchorClick = (e) => {
      const a = e.target.closest('a');
      if (
        a &&
        a.href &&
        a.getAttribute('target') !== '_blank' &&
        !e.defaultPrevented &&
        e.button === 0 && // 仅左键点击
        !e.metaKey &&
        !e.ctrlKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        const url = new URL(a.href);
        if (url.origin === window.location.origin) {
          e.preventDefault();
          window.history.pushState(null, '', url.pathname + url.search + url.hash);
          handleLocationChange();
        }
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    document.addEventListener('click', handleAnchorClick);

    // 首次加载初始化路由状态
    handleLocationChange();

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      document.removeEventListener('click', handleAnchorClick);
    };
  }, []);

  // 渲染其他懒加载页面
  const renderOtherPages = () => {
    switch (currentPage) {
      case 'home':
      case 'shop':
        return null; // 这两个页面在下方常驻渲染
      case 'product': {
        let activeProduct = selectedProduct;
        
        // 若状态丢失（如刷新或直接访问），通过 URL 的 id 参数恢复商品详情页
        if (!activeProduct) {
          const params = new URLSearchParams(window.location.search);
          const productId = parseInt(params.get('id'), 10);
          if (productId) {
            activeProduct = productData.find(p => p.id === productId);
          }
        }

        const handleSetSelectedProduct = (prod) => {
          setSelectedProduct(prod);
          if (prod) {
            window.history.pushState(null, '', `/product?id=${prod.id}`);
            window.dispatchEvent(new Event('popstate'));
          }
        };

        return (
          <ProductDetail
            product={activeProduct}
            setSelectedProduct={handleSetSelectedProduct}
          />
        );
      }
      case 'showcase':
        return <Showcase />;
      case 'story':
        return <Story />;
      case 'draft':
        return <Draft />;
      case 'press':
        return <Press />;
      case 'qa':
        return <QA />;
      case 'connect':
        return <Connect />;
      case 'stockists':
        return <Stockists />;
      case 'cart':
        return <Cart />;
      case 'checkout-success':
        return <CheckoutSuccess />;
      default:
        return <Home />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        currentPage={currentPage}
      />
      <main className="flex-grow">
        {/* 常驻渲染核心页面，避免频繁销毁和重建 DOM 导致的卡顿 */}
        <div style={{ display: currentPage === 'home' ? 'block' : 'none' }}>
          <Home />
        </div>
        <div style={{ display: currentPage === 'shop' ? 'block' : 'none' }}>
          <Shop setSelectedProduct={setSelectedProduct} />
        </div>

        {/* 懒加载其他页面 */}
        <Suspense fallback={<LoadingFallback />}>
          {renderOtherPages()}
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </LanguageProvider>
  );
}